import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { BrowserRouter as Router, Route, Redirect } from "react-router-dom";
import SelectClass from "../ChildComponent/SelectClass";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import SessionManager from "../Utils/SessionManager";
import MyLoader from "../Spinner/MyLoader";

// Utility function to parse API response (handles ?> prefix and other edge cases)
const parseApiResponse = (response) => {
  if (typeof response === 'string') {
    try {
      let cleanedResponse = response.trim();
      const jsonStart = cleanedResponse.search(/[{\[]/);
      if (jsonStart > 0) {
        cleanedResponse = cleanedResponse.substring(jsonStart);
      }
      return JSON.parse(cleanedResponse);
    } catch (e) {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch (e2) {
          return null;
        }
      }
      return null;
    }
  }
  return response;
};

// Utility function to get student ID consistently
const getStudentId = (student) => {
  return student?.student_enrollment_no || student?.id;
};

// Utility function to get subject ID consistently
const getSubjectId = (subject) => {
  return subject?.subject_id || subject?.id;
};

const AlternateSubject = () => {
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [className, setClassName] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState("");
  const [altSubjects, setAltSubjects] = useState([]); // List of alternative subjects
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [selectedSubjectName, setSelectedSubjectName] = useState("");
  const [students, setStudents] = useState([]); // List of students for selected subject
  const [studentStatus, setStudentStatus] = useState({}); // Track student selection status

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getClassStudentOfAltSubject = useCallback((subject_id) => {
    if (!classId || !sectionId || !sessionId || !subject_id) {
      return;
    }

    setLoading(true);
    setStudents([]);
    setStudentStatus({});
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const API_NAME = "getClassStudentOfAltSubject.php";
    const URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + API_NAME;

    axios
      .get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          session_id: sessionId,
          class_id: classId,
          section_id: sectionId,
          subject_id: subject_id,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          const studentList = response.data || [];
          setStudents(studentList);
          
          // Initialize student status based on current status
          const initialStatus = {};
          studentList.forEach((student) => {
            const studentId = getStudentId(student);
            const currentStatus = student.status || "Inactive";
            initialStatus[studentId] = currentStatus === "Active" || currentStatus === "active";
          });
          setStudentStatus(initialStatus);
        } else {
          myToast.notify_warning(response.api_remark || "No students found");
        }
      })
      .catch((error) => {
        setLoading(false);
        myToast.notify_error("Failed to load students");
      });
  }, [classId, sectionId, sessionId]);

  const handleSubjectSelect = useCallback((subjectId, subjectName) => {
    setSelectedSubjectId(subjectId);
    setSelectedSubjectName(subjectName);
    getClassStudentOfAltSubject(subjectId);
  }, [getClassStudentOfAltSubject]);

  const getClassAltSubject = useCallback((class_id, section_id, session_id) => {
    setLoading(true);
    setAltSubjects([]);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const API_NAME = "getClassAltSubject.php";
    const URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + API_NAME;

    axios
      .get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          session_id: session_id,
          class_id: class_id,
          section_id: section_id,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setAltSubjects(response.data || []);
        } else {
          myToast.notify_warning(response.api_remark || "No alternative subjects found");
        }
      })
      .catch((error) => {
        setLoading(false);
        myToast.notify_error("Failed to load alternative subjects");
      });
  }, []);

  const callBackClassSelect = useCallback((
    class_id,
    section_id,
    session_id,
    selected_position,
    class_name,
    section_name,
    session_name
  ) => {
    setClassId(class_id);
    setClassName(class_name);
    setSectionId(section_id);
    setSessionId(session_id);
    setSelectedClassPosition(selected_position);
    setSelectedSubjectId(null);
    setSelectedSubjectName("");
    setStudents([]);
    setStudentStatus({});
    getClassAltSubject(class_id, section_id, session_id);
  }, [getClassAltSubject]);

  const handleStudentToggle = useCallback((studentId) => {
    setStudentStatus((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  }, []);

  // Helper function to build student list with changed statuses only
  const buildChangedStudentList = useCallback(() => {
    const studentList = [];
    students.forEach((student) => {
      const studentId = getStudentId(student);
      const oldStatus = (student.status || "Inactive").toLowerCase();
      const newStatus = studentStatus[studentId] ? "Active" : "Inactive";
      
      // Only include if status changed (case-insensitive comparison)
      const oldStatusNormalized = oldStatus === "active" ? "Active" : "Inactive";
      if (oldStatusNormalized !== newStatus) {
        studentList.push({
          student_id: studentId,
          subject_id: selectedSubjectId,
          status_new: newStatus,
        });
      }
    });
    return studentList;
  }, [students, studentStatus, selectedSubjectId]);

  // Helper function to handle API response
  const handleApiResponse = useCallback((response, successMessage, errorMessage, onSuccess) => {
    const parsedResponse = parseApiResponse(response);
    const apiStatus = parsedResponse?.api_status || parsedResponse?.apiStatus || "";
    
    if (apiStatus && apiStatus.toUpperCase() === "OK") {
      myToast.notify_success(successMessage);
      if (onSuccess) onSuccess();
    } else {
      const errorMsg = parsedResponse?.api_remark || parsedResponse?.apiRemark || parsedResponse?.message || errorMessage;
      myToast.notify_error(errorMsg);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedSubjectId) {
      myToast.notify_warning("Please select an alternative subject first");
      return;
    }

    if (!sessionId || !classId || !sectionId) {
      myToast.notify_warning("Please select a class and section first");
      return;
    }

    const studentList = buildChangedStudentList();

    if (studentList.length === 0) {
      myToast.notify_warning("No changes to update");
      return;
    }

    const staffDetails = SessionManager.getStaffDetails();
    if (!staffDetails?.staff_id) {
      myToast.notify_error("Staff information not found. Please login again.");
      return;
    }

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    if (!SCHOOL_CODE) {
      myToast.notify_error("School code not found");
      return;
    }

    const API_NAME = "updateClassStudentAltSubjects.php";
    const URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + API_NAME;

    setLoading(true);

    const formData = new FormData();
    formData.append("staff_id", staffDetails.staff_id);
    formData.append("session_id", sessionId);
    formData.append("class_id", classId);
    formData.append("section_id", sectionId);
    formData.append("student_list", JSON.stringify(studentList));
    formData.append("school_code", SCHOOL_CODE);

    axios
      .post(URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => parseApiResponse(res.data))
      .then((response) => {
        setLoading(false);
        handleApiResponse(
          response,
          `Successfully updated ${studentList.length} student(s)`,
          "Failed to update students",
          () => getClassStudentOfAltSubject(selectedSubjectId)
        );
      })
      .catch((error) => {
        setLoading(false);
        const errorMsg = error.response?.data?.api_remark || "Failed to update students. Please try again.";
        myToast.notify_error(errorMsg);
      });
  }, [selectedSubjectId, sessionId, classId, sectionId, buildChangedStudentList, handleApiResponse, getClassStudentOfAltSubject]);

  const hasStudents = useMemo(() => students && students.length > 0, [students]);
  const hasAltSubjects = useMemo(() => altSubjects && altSubjects.length > 0, [altSubjects]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <Router>
      <Route
        path={"/AdminMain/AlternateSubject"}
        exact
        render={() => (
          <div className="content" style={{ background: COLORS.white }}>
            <div className="content-header">
              <div className="leftside-content-header">
                <ul className="breadcrumbs">
                  <li>
                    <i className="fa fa-home" aria-hidden="true" />
                    <a href="#"> Alternative Subject </a>
                  </li>
                </ul>
              </div>
            </div>

            <div
              className="bg_page_grey_rounded box_shadow"
              style={{ 
                padding: "15px",
                margin: "15px",
                borderRadius: "16px",
                background: COLORS.white,
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)"
              }}
            >
              <div style={{ marginBottom: "15px" }}>
                <SelectClass action={callBackClassSelect} selected_position="0" />
              </div>

              {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                  <MyLoader />
                </div>
              ) : (
                <>
                  {/* Alternative Subjects Selection */}
                  {hasAltSubjects && (
                    <div style={{
                      background: `linear-gradient(135deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 100%)`,
                      borderRadius: "12px",
                      padding: "15px",
                      marginBottom: "20px",
                      border: `2px solid ${COLORS.primary_color_shade3}`,
                    }}>
                      <h3 style={{
                        margin: "0 0 15px 0",
                        fontSize: "18px",
                        fontWeight: "600",
                        color: COLORS.primary_color_shade7,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                      }}>
                        <i className="fa fa-book" />
                        Select Alternative Subject
                      </h3>
                      <div style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px"
                      }}>
                        {altSubjects.map((subject, index) => {
                          const subjectId = getSubjectId(subject);
                          const subjectName = subject.subject_name || subject.name || `Subject ${index + 1}`;
                          const isSelected = selectedSubjectId === subjectId;
                          
                          return (
                            <button
                              key={`subject-${subjectId}-${index}`}
                              onClick={() => handleSubjectSelect(subjectId, subjectName)}
                              style={{
                                padding: "10px 20px",
                                fontSize: "14px",
                                fontWeight: "600",
                                borderRadius: "8px",
                                border: `2px solid ${isSelected ? COLORS.primary_color_shade5 : COLORS.grey3}`,
                                background: isSelected 
                                  ? `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`
                                  : COLORS.white,
                                color: isSelected ? COLORS.white : COLORS.primary_color_shade7,
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                boxShadow: isSelected 
                                  ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                                  : "0 2px 6px rgba(0, 0, 0, 0.1)",
                                transform: isSelected ? "translateY(-2px)" : "translateY(0)",
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.target.style.transform = "translateY(-2px)";
                                  e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)";
                                }
                              }}
                            >
                              {subjectName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Students List */}
                  {selectedSubjectId && (
                    <>
                      {hasStudents ? (
                        <>
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: "12px",
                            marginBottom: "20px"
                          }}>
                            {students.map((student, index) => {
                              const studentId = getStudentId(student);
                              const isSelected = studentStatus[studentId] || false;
                              const wasAlreadyActive = student.status === "Active" || student.status === "active";
                              
                              return (
                                <div
                                  key={`student-${studentId}-${index}`}
                                  onClick={() => handleStudentToggle(studentId)}
                                  style={{
                                    background: COLORS.white,
                                    borderRadius: "12px",
                                    padding: "15px",
                                    boxShadow: isSelected 
                                      ? "0 4px 12px rgba(76, 175, 80, 0.2)"
                                      : "0 2px 8px rgba(0, 0, 0, 0.08)",
                                    border: `2px solid ${isSelected ? COLORS.green3 : COLORS.grey2}`,
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    position: "relative",
                                    transform: "translateY(0)",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = isSelected
                                      ? "0 6px 16px rgba(76, 175, 80, 0.3)"
                                      : "0 4px 12px rgba(0, 0, 0, 0.12)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = isSelected
                                      ? "0 4px 12px rgba(76, 175, 80, 0.2)"
                                      : "0 2px 8px rgba(0, 0, 0, 0.08)";
                                  }}
                                >
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px"
                                  }}>
                                    <div style={{
                                      width: "50px",
                                      height: "50px",
                                      borderRadius: "50%",
                                      background: `linear-gradient(135deg, ${COLORS.primary_color_shade3} 0%, ${COLORS.primary_color_shade5} 100%)`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: COLORS.white,
                                      fontSize: "18px",
                                      fontWeight: "600",
                                      border: `3px solid ${isSelected ? COLORS.green3 : COLORS.primary_color_shade3}`,
                                    }}>
                                      {student.stu_name ? student.stu_name.charAt(0).toUpperCase() : "S"}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{
                                        fontSize: "15px",
                                        fontWeight: "600",
                                        color: COLORS.black9,
                                        marginBottom: "4px"
                                      }}>
                                        {student.stu_name || "N/A"}
                                      </div>
                                      <div style={{
                                        fontSize: "12px",
                                        color: COLORS.grey5,
                                        marginBottom: "2px"
                                      }}>
                                        Father: {student.father_name || "N/A"}
                                      </div>
                                      <div style={{
                                        fontSize: "12px",
                                        color: COLORS.grey5
                                      }}>
                                        Roll No: {student.student_roll_no || "N/A"} | Enroll: {student.student_enrollment_no || student.id}
                                      </div>
                                    </div>
                                    <div style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "50%",
                                      background: isSelected 
                                        ? `linear-gradient(135deg, ${COLORS.green2} 0%, ${COLORS.green3} 100%)`
                                        : COLORS.grey3,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: COLORS.white,
                                      fontSize: "16px",
                                      transition: "all 0.3s ease",
                                    }}>
                                      {isSelected ? (
                                        <i className="fa fa-check" />
                                      ) : (
                                        <i className="fa fa-times" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Submit Button */}
                          <div style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px"
                          }}>
                            <button
                              onClick={handleSubmit}
                              disabled={loading}
                              style={{
                                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
                                color: COLORS.white,
                                border: "none",
                                borderRadius: "10px",
                                padding: "12px 32px",
                                fontSize: "16px",
                                fontWeight: "600",
                                cursor: loading ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                                transition: "all 0.3s ease",
                                opacity: loading ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!loading) {
                                  e.target.style.transform = "translateY(-2px)";
                                  e.target.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.4)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!loading) {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
                                }
                              }}
                            >
                              <i className="fa fa-save" />
                              Submit
                            </button>
                          </div>
                        </>
                      ) : selectedSubjectId && !loading ? (
                        <div style={{
                          textAlign: "center",
                          padding: "60px 20px",
                          color: COLORS.grey5
                        }}>
                          <i className="fa fa-users" style={{ fontSize: "64px", marginBottom: "20px", opacity: 0.3 }} />
                          <h3 style={{ margin: "0 0 10px 0", color: COLORS.grey5 }}>
                            No Students Found
                          </h3>
                          <p style={{ margin: 0, fontSize: "14px" }}>
                            No students found for this alternative subject
                          </p>
                        </div>
                      ) : null}
                    </>
                  )}

                  {!hasAltSubjects && !loading && (
                    <div style={{
                      textAlign: "center",
                      padding: "60px 20px",
                      color: COLORS.grey5
                    }}>
                      <i className="fa fa-book" style={{ fontSize: "64px", marginBottom: "20px", opacity: 0.3 }} />
                      <h3 style={{ margin: "0 0 10px 0", color: COLORS.grey5 }}>
                        No Alternative Subjects Found
                      </h3>
                      <p style={{ margin: 0, fontSize: "14px" }}>
                        Please select a class and section to view alternative subjects
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      />
    </Router>
  );
};

export default AlternateSubject;
